/**
 * Interceptores compartidos.
 *
 * Request: adjunta el token JWT de la sesión (`@shared/auth/sesion`).
 * Response: en 401, dispara `onUnauthorized` (inyectado por `AuthProvider`),
 * que se encarga de limpiar estado y redirigir con `useNavigate` sin recargar.
 */
import { AxiosInstance } from 'axios';

import { borrarSesion, leerToken } from '../auth/sesion';
import { logger } from '../utils/logger';

type UnauthorizedHandler = () => void;

let unauthorizedHandler: UnauthorizedHandler = () => {
  // Fallback seguro mientras `AuthProvider` no se ha montado.
  borrarSesion();
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.assign('/login');
  }
};

export function setUnauthorizedHandler(fn: UnauthorizedHandler): void {
  unauthorizedHandler = fn;
}

export function registerInterceptors(instance: AxiosInstance): void {
  instance.interceptors.request.use((config) => {
    const token = leerToken();
    if (token) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }
    return config;
  });

  instance.interceptors.response.use(
    (response) => {
      // Propagar el request-id para soporte.
      const rid = response.headers['x-request-id'];
      if (rid) response.config.headers.set?.('X-Request-Id', String(rid));
      return response;
    },
    (error) => {
      if (error.response?.status === 401) {
        logger.warn('HTTP 401 recibido', { url: error.config?.url });
        try {
          unauthorizedHandler();
        } catch (e) {
          logger.error('unauthorizedHandler lanzó una excepción', e);
        }
      }
      return Promise.reject(error);
    }
  );
}
